/**
 * AdvancedSymbologyEditor
 *
 * Main dialog component that integrates categorized symbology, graduated symbology,
 * and feature labeling into a tabbed interface. Provides a unified entry point
 * for all advanced symbology operations.
 *
 * **Validates: Requirements 41, 42, 43**
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, Palette, BarChart, Label } from '@mui/icons-material';
import type {
  Symbology,
  CategorizedRule,
  GraduatedRule,
  LabelConfig,
  ClassificationMethod,
  CategoryGroup,
} from '../types/symbology';
import CategorizedSymbologyPanel from './CategorizedSymbologyPanel';
import GraduatedSymbologyPanel from './GraduatedSymbologyPanel';
import LabelConfigPanel from './LabelConfigPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdvancedSymbologyEditorProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The layer name shown in the dialog title. */
  layerName: string;
  /** The geometry type of the layer. */
  geometryType: 'Point' | 'LineString' | 'Polygon';
  /** Current symbology configuration. */
  currentSymbology?: Symbology;
  /** Features with their properties for extracting values. */
  features: Array<{ properties: Record<string, any> }>;
  /** Called when the user applies symbology changes. */
  onApply: (symbology: Symbology) => void;
  /** Called when the user closes the dialog. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Tab panel helper
// ---------------------------------------------------------------------------

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`symbology-tabpanel-${index}`}
    aria-labelledby={`symbology-tab-${index}`}
  >
    {value === index && children}
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AdvancedSymbologyEditor: React.FC<AdvancedSymbologyEditorProps> = ({
  open,
  layerName,
  geometryType,
  currentSymbology,
  features,
  onApply,
  onClose,
}) => {
  // Determine initial tab based on current symbology type
  const getInitialTab = () => {
    if (currentSymbology?.type === 'categorized') return 0;
    if (currentSymbology?.type === 'graduated') return 1;
    return 0;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  // Extract available attribute fields from features
  const attributeFields = useMemo(() => {
    const fieldSet = new Set<string>();
    for (const feature of features) {
      if (feature.properties) {
        for (const key of Object.keys(feature.properties)) {
          // Skip internal fields
          if (!key.startsWith('_') && key !== 'geometry') {
            fieldSet.add(key);
          }
        }
      }
    }
    return Array.from(fieldSet).sort();
  }, [features]);

  // Handle categorized symbology apply
  const handleCategorizedApply = useCallback(
    (field: string, rules: CategorizedRule[], groups?: CategoryGroup[]) => {
      const symbology: Symbology = {
        type: 'categorized',
        categorizedRules: rules,
        categoryGroups: groups,
        opacity: currentSymbology?.opacity ?? 1,
        labelConfig: currentSymbology?.labelConfig,
      };
      onApply(symbology);
    },
    [currentSymbology, onApply]
  );

  // Handle graduated symbology apply
  const handleGraduatedApply = useCallback(
    (
      field: string,
      rules: GraduatedRule[],
      method: ClassificationMethod,
      numClasses: number,
      colorRampId: string
    ) => {
      const symbology: Symbology = {
        type: 'graduated',
        graduatedRules: rules,
        opacity: currentSymbology?.opacity ?? 1,
        labelConfig: currentSymbology?.labelConfig,
      };
      onApply(symbology);
    },
    [currentSymbology, onApply]
  );

  // Handle label config apply
  const handleLabelApply = useCallback(
    (config: LabelConfig) => {
      const symbology: Symbology = {
        ...(currentSymbology || { type: 'simple', opacity: 1 }),
        labelConfig: config,
      };
      onApply(symbology);
    },
    [currentSymbology, onApply]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="advanced-symbology-title"
    >
      {/* Title */}
      <DialogTitle
        id="advanced-symbology-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette color="primary" />
          <Box>
            <Typography variant="h6" component="span">
              Map Colors & Visual Styles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
              {layerName} · {geometryType}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close advanced symbology editor">
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label="Symbology type tabs"
        >
          <Tab
            icon={<Palette fontSize="small" />}
            iconPosition="start"
            label="Color by Category"
            id="symbology-tab-0"
            aria-controls="symbology-tabpanel-0"
          />
          <Tab
            icon={<BarChart fontSize="small" />}
            iconPosition="start"
            label="Color by Value Range"
            id="symbology-tab-1"
            aria-controls="symbology-tabpanel-1"
          />
          <Tab
            icon={<Label fontSize="small" />}
            iconPosition="start"
            label="Labels"
            id="symbology-tab-2"
            aria-controls="symbology-tabpanel-2"
          />
        </Tabs>
      </Box>

      {/* Tab content */}
      <DialogContent sx={{ p: 0 }}>
        <TabPanel value={activeTab} index={0}>
          <CategorizedSymbologyPanel
            fields={attributeFields}
            features={features}
            selectedField={
              currentSymbology?.type === 'categorized'
                ? currentSymbology.categorizedRules?.[0]?.attributeField
                : undefined
            }
            currentRules={currentSymbology?.categorizedRules}
            currentGroups={currentSymbology?.categoryGroups}
            onApply={handleCategorizedApply}
            onCancel={onClose}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <GraduatedSymbologyPanel
            fields={attributeFields}
            features={features}
            selectedField={
              currentSymbology?.type === 'graduated'
                ? currentSymbology.graduatedRules?.[0]?.attributeField
                : undefined
            }
            currentRules={currentSymbology?.graduatedRules}
            onApply={handleGraduatedApply}
            onCancel={onClose}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <LabelConfigPanel
            fields={attributeFields}
            currentConfig={currentSymbology?.labelConfig}
            geometryType={geometryType}
            onApply={handleLabelApply}
            onCancel={onClose}
          />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedSymbologyEditor;
